import { Controller, useForm } from 'react-hook-form';
import React, { useEffect, useState } from 'react';

import Alert from '@mui/material/Alert';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import MenuItem from '@mui/material/MenuItem';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import PermIdentityOutlinedIcon from '@mui/icons-material/PermIdentityOutlined';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { Link as RouterLink } from 'react-router-dom';
import SaveIcon from '@mui/icons-material/Save';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import Select from '@mui/material/Select';
import Snackbar from '@mui/material/Snackbar';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import Typography from '@mui/material/Typography';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import { useCandidateContext } from '../providers/CandidateProvider';
import { useProfile } from '../providers/ProfileProvider';
import { seniorityLevels } from '../config';
import neutralAvatarImg from '../images/neutral-avatar.svg';

const NeutralAvatar = () => <img src={neutralAvatarImg} alt="" width="120" height="120" />;

const CardHeader = ({title, children, subtitle }) => {
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <div className='iconBadge'>
          {children}
        </div>
        <Typography variant="xl" fontWeight="medium" component="h3">
          {title}
        </Typography>
      </Box>
      {subtitle ? (
        <Typography
          variant="md"
          component="p"
          color="text.secondary"
          sx={{ mb: 1 }}
        >
          {subtitle}
        </Typography>
      ) : null}
      <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.08)', mb: 2 }} />
    </>
  )
}



const SIDE_ASSIGNMENT_OPTIONS = [
  'Up to 10 hours per week',
  'Up to 20 hours per week',
  'More than 20 hours per week'
];

const ENGAGEMENT_TYPES = ['Full-time', 'Part-time', 'Contract', 'Freelance'];

const VIDEO_PRESENTATION_OPTIONS = [
  "Yes, I'm interested",
  "No, not interested",
  "Maybe Later"
];

const SECONDARY_ROLES_OPTIONS = [
  'Front-end Developer',
  'Back-end Developer',
  'UI/UX Design',
  'Mobile Developer',
  'Full-stack Developer',
  'DevOps Engineer',
  'QA Engineer'
];

const MAIN_ROLE_OPTIONS = [
  'Mobile Developer',
  'Full-stack Developer',
  'Front-end Developer',
  'Back-end Developer',
  'Business Analyst',
  'UI/UX Designer',
  'Software Tester/QA Engineer',
  'QA Lead/Test Manager',
  'Project Manager',
  'Enterprise Architect',
  'Software Architect',
  'Scrum Master',
  'DevOps Engineer',
  'Team Lead/Tech Lead',
  'DBA/DB Developer',
  'Other'
];

const Profile = () => {
  const { candidate, loading: candidateLoading, error: candidateError, saveCandidate, refreshCandidate } = useCandidateContext();
  const profile = useProfile();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [secondaryRoles, setSecondaryRoles] = useState([]);
  const [engagementTypes, setEngagementTypes] = useState([]);
  const [noticeTime, setNoticeTime] = useState('1 Month');
  const [noticeTimeOther, setNoticeTimeOther] = useState('');
  const [secondaryRoleInput, setSecondaryRoleInput] = useState('');

  // Format date for input field
  const formatDateForInput = (date) => {
    if (!date) return '';
    if (typeof date === 'string') return date.split('T')[0];
    return new Date(date).toISOString().split('T')[0];
  };

  // Get full name from user profile
  const getFullName = () => {
    if (profile?.firstName && profile?.lastName) {
      return `${profile.firstName} ${profile.lastName}`.toUpperCase();
    }
    return candidate?.user?.firstName && candidate?.user?.lastName
      ? `${candidate.user.firstName} ${candidate.user.lastName}`.toUpperCase()
      : 'JANE DOE';
  };

  // Get location from user profile
  const getLocation = () => {
    if (profile?.city && profile?.country) {
      let countryLabel = '';

      if (typeof profile.country === 'string') {
        try {
          const parsed = JSON.parse(profile.country);
          countryLabel = parsed?.label || profile.country;
        } catch {
          countryLabel = profile.country;
        }
      } else if (profile.country?.label) {
        countryLabel = profile.country.label;
      } else {
        countryLabel = String(profile.country);
      }

      return `${profile.city}, ${countryLabel}`;
    }
    return candidate?.location || 'Nairobi, Kenya';
  };

  // Get date of birth from user profile
  const getDateOfBirth = () => {
    if (profile?.dateOfBirth) {
      return formatDateForInput(profile.dateOfBirth);
    }
    return candidate?.born || '1990-05-15';
  };

  const defaultValues = {
    fullName: getFullName(),
    mainRole: candidate?.mainRole || profile?.primaryRole || 'Senior Frontend Developer',
    location: getLocation(),
    born: getDateOfBirth(),
    seniority: candidate?.seniority || profile?.seniority || 'Senior (5+ years)',
    noticeTime: candidate?.noticeTime || '1 Month',
    sideAssignments: candidate?.sideAssignments || 'Up to 10 hours per week',
    videoPresentation: candidate?.videoPresentation || 'Maybe Later',
    includeInTalentPool: candidate?.includeInTalentPool !== undefined ? candidate.includeInTalentPool : false,
    profilePause: candidate?.profilePause !== undefined ? candidate.profilePause : false,
  };

  const { control, handleSubmit, reset } = useForm({
    defaultValues,
  });

  // Update form when candidate data loads
  useEffect(() => {
    if (candidate) {
      setSecondaryRoles(candidate.secondaryRoles || []);
      setEngagementTypes(candidate.engagementTypes || []);
      setNoticeTime(candidate.noticeTime || '1 Month');
      setNoticeTimeOther(candidate.noticeTimeOther || '');
      
      // Format date for input field
      const formatDate = (date) => {
        if (!date) return '';
        if (typeof date === 'string') return date.split('T')[0];
        return new Date(date).toISOString().split('T')[0];
      };

      // Get full name from user profile
      const fullName = profile?.firstName && profile?.lastName
        ? `${profile.firstName} ${profile.lastName}`.toUpperCase()
        : candidate?.user?.firstName && candidate?.user?.lastName
        ? `${candidate.user.firstName} ${candidate.user.lastName}`.toUpperCase()
        : 'JANE DOE';

      // Get location from user profile
      const location = profile?.city && profile?.country
        ? `${profile.city}, ${profile.country}`
        : candidate?.location || 'Nairobi, Kenya';

      // Get date of birth from user profile
      const dateOfBirth = profile?.dateOfBirth
        ? formatDate(profile.dateOfBirth)
        : candidate?.born || '1990-05-15';
      
      reset({
        fullName: fullName,
        mainRole: candidate.mainRole || profile?.primaryRole || '',
        location: location,
        born: dateOfBirth,
        seniority: candidate.seniority || profile?.seniority || '',
        noticeTime: candidate.noticeTime || '1 Month',
        sideAssignments: candidate.sideAssignments || '',
        videoPresentation: candidate.videoPresentation || 'Maybe Later',
        includeInTalentPool: candidate.includeInTalentPool !== undefined ? candidate.includeInTalentPool : false,
        profilePause: candidate.profilePause !== undefined ? candidate.profilePause : false,
      });
    }
  }, [candidate, profile, reset]);

  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setSecondaryRoleInput('');
    if (candidate) {
      setSecondaryRoles(candidate.secondaryRoles || []);
      setEngagementTypes(candidate.engagementTypes || []);
      setNoticeTime(candidate.noticeTime || '1 Month');
      setNoticeTimeOther(candidate.noticeTimeOther || '');
      reset(defaultValues);
    } else {
      reset(defaultValues);
      setSecondaryRoles([]);
      setEngagementTypes([]);
      setNoticeTime('1 Month');
      setNoticeTimeOther('');
    }
  };

  const onSubmit = async (data) => {
    try {
      setIsSaving(true);
      
      // Prepare candidate data
      const candidateData = {
        mainRole: data.mainRole,
        secondaryRoles: secondaryRoles,
        noticeTime: noticeTime,
        noticeTimeOther: noticeTime === 'Other' ? noticeTimeOther : undefined,
        sideAssignments: data.sideAssignments,
        engagementTypes: engagementTypes,
        videoPresentation: data.videoPresentation,
        includeInTalentPool: data.includeInTalentPool,
        profilePause: data.profilePause,
        location: data.location,
      };

      // Remove undefined fields
      Object.keys(candidateData).forEach(
        (key) => candidateData[key] === undefined && delete candidateData[key]
      );

      await saveCandidate(candidateData);
      
      setSnackbar({
        open: true,
        message: 'Profile updated successfully!',
        severity: 'success'
      });
      setIsEditMode(false);
      await refreshCandidate();
    } catch (error) {
      console.error('Failed to save profile:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || error.response?.data?.message || 'Failed to save profile. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveSecondaryRole = (roleToRemove) => {
    setSecondaryRoles(secondaryRoles.filter(role => role !== roleToRemove));
  };

  const handleAddSecondaryRole = (newRole) => {
    if (newRole && !secondaryRoles.includes(newRole)) {
      setSecondaryRoles([...secondaryRoles, newRole]);
    }
  };

  const handleRemoveEngagementType = (typeToRemove) => {
    setEngagementTypes(engagementTypes.filter(type => type !== typeToRemove));
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (candidateLoading) {
    return (
      <main className="main-section">
        <div className="content-section nc-profile">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        </div>
      </main>
    );
  }

  return (
    <main className="main-section">
      <div className="content-section nc-profile">
        {candidateError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {candidateError}
          </Alert>
        )}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
        <RouterLink to="/" className="nc-back-link">
          <ArrowBackIcon />
          <Typography variant="md" component="span">Go Home</Typography>
        </RouterLink>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="displaySm" component="h1">Profile</Typography>
          {!isEditMode ? (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleEditClick}
              sx={{
                textTransform: 'none',
                borderColor: '#483C3B',
                color: '#483C3B',
                '&:hover': {
                  borderColor: '#483C3B',
                  backgroundColor: 'rgba(72, 60, 59, 0.04)',
                },
              }}
            >
              Edit Profile
            </Button>
          ) : (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                onClick={handleSubmit(onSubmit)}
                disabled={isSaving}
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#E84610',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: '#d63d0e',
                  },
                }}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<CloseIcon />}
                onClick={handleCancel}
                sx={{
                  textTransform: 'none',
                  borderColor: '#EAE7E6',
                  color: '#483C3B',
                  backgroundColor: '#EAE7E6',
                  '&:hover': {
                    borderColor: '#EAE7E6',
                    backgroundColor: '#d5d2d1',
                  },
                }}
              >
                Cancel
              </Button>
            </Box>
          )}
        </Box>
        <Card id="profile-bio-card" sx={{ py: 3 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 3,
            px: 2
          }}>
            <Box sx={{ 
              position: 'relative', 
              display: 'inline-block', 
              width: 120, 
              height: 120,
              flexShrink: 0
            }}>
              <NeutralAvatar />
              {isEditMode && (
                <IconButton
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    backgroundColor: '#483C3B',
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: '#3a302f',
                    },
                    width: 32,
                    height: 32,
                    border: '2px solid #fff',
                  }}
                >
                  <EditIcon sx={{ fontSize: 18 }} />
                </IconButton>
              )}
            </Box>
            <div className='nc-bio-text' style={{ flex: 1 }}>
            {!isEditMode ? (
              <>
            <div className="nc-profile-title">
                  <Typography variant="displaySm" component="p" className="text-uppercase">
                    {getFullName()}
                  </Typography>
                  <Typography variant="md" component="p">
                    {candidate?.mainRole || profile?.primaryRole || 'Senior Front-End Developer'}
                  </Typography>
            </div>
            <div className='nc-bio-tags'>
              <span className='nc-bio-tag'>
                <LocationOnOutlinedIcon color='primary' />
                    <Typography variant="sm" fontWeight="light" component="span">
                      {getLocation()}
                    </Typography>
              </span>
              <span className='nc-bio-tag'>
                <TodayOutlinedIcon color="primary" />
                    <Typography variant="sm" fontWeight="light" component="span">
                      Born {getDateOfBirth()}
                    </Typography>
              </span>
            </div>
              </>
            ) : (
              <Box sx={{ width: '100%', maxWidth: '600px' }}>
                <Controller
                  name="fullName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Full Name"
                      size="medium"
                      sx={{ mb: 2 }}
                    />
                  )}
                />
                <Controller
                  name="mainRole"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Autocomplete
                      freeSolo
                      value={value || ''}
                      options={MAIN_ROLE_OPTIONS}
                      onChange={(event, newValue) => {
                        onChange(newValue || '');
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Main Role"
                          size="medium"
                          placeholder="Select or type your main role"
                        />
                      )}
                      sx={{ mb: 2 }}
                    />
                  )}
                />
                <Controller
                  name="location"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Location"
                      size="medium"
                      InputProps={{
                        startAdornment: (
                          <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                            <LocationOnOutlinedIcon sx={{ color: '#483C3B' }} />
                          </Box>
                        ),
                      }}
                      sx={{ mb: 2 }}
                    />
                  )}
                />
                <Controller
                  name="born"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Born"
                      type="date"
                      size="medium"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                            <TodayOutlinedIcon sx={{ color: '#483C3B' }} />
                          </Box>
                        ),
                      }}
                    />
                  )}
                />
              </Box>
            )}
          </div>
          </Box>
        </Card>
        <Card id="profile-info-card">
          <CardContent>
            <CardHeader title="Personal Information">
              <PermIdentityOutlinedIcon />
            </CardHeader>
            <div className="nc-card-body">
              {!isEditMode ? (
                <>
                  {secondaryRoles.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="sm" fontWeight="light" component="p">Secondary Roles</Typography>
                      <div className='nc-badge-list'>
                        {secondaryRoles.map((role) => (
                          <Chip key={role} variant="outlined" label={role} />
                        ))}
                      </div>
                    </Box>
                  )}
                  <div>
                    <Typography variant="sm" fontWeight="light" component="p">Seniority Level</Typography>
                    <Typography variant="md" component="p">
                      {candidate?.seniority || profile?.seniority || 'Senior (5+ years)'}
                    </Typography>
                  </div>
                </>
              ) : (
                <>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                      {secondaryRoles.map((role) => (
                        <Chip
                          key={role}
                          label={role}
                          onDelete={() => handleRemoveSecondaryRole(role)}
                          sx={{ backgroundColor: '#EAE7E6' }}
                        />
                      ))}
                    </Box>
                    <Autocomplete
                      freeSolo
                      value={null}
                      inputValue={secondaryRoleInput}
                      options={SECONDARY_ROLES_OPTIONS.filter(role => !secondaryRoles.includes(role))}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Add secondary roles"
                          size="medium"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <SearchOutlinedIcon sx={{ color: '#483C3B', mr: 1 }} />
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      onChange={(event, newValue) => {
                        if (newValue) {
                          handleAddSecondaryRole(newValue);
                          setSecondaryRoleInput('');
                        }
                      }}
                      onInputChange={(event, newInputValue) => {
                        if (event && event.type === 'keydown' && event.key === 'Enter' && newInputValue) {
                          handleAddSecondaryRole(newInputValue);
                          setSecondaryRoleInput('');
                        } else {
                          setSecondaryRoleInput(newInputValue);
                        }
                      }}
                    />
                  </Box>
                  <Controller
                    name="seniority"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth size="medium">
                        <InputLabel>Seniority Level</InputLabel>
                        <Select {...field} label="Seniority Level" value={field.value || ''}>
                          {seniorityLevels.map((level) => (
                            <MenuItem key={level} value={level}>{level}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </>
              )}
            </div>
          </CardContent>
        </Card>
        <Card id="profile-info-card" sx={{ marginTop: 2 }}>
          <CardContent>
            <CardHeader title="Opportunities & Visibility">
              <WorkOutlineOutlinedIcon /> 
            </CardHeader>
            <div className="nc-card-body">
              {!isEditMode ? (
                <>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="md" component="p" sx={{ fontWeight: 'medium' }}>Notice Time</Typography>
                    <Typography variant="sm" component="p" sx={{ color: 'text.secondary', mb: 0.5 }}>
                      Required notice period for leaving current position
                    </Typography>
                    <Typography variant="md" component="p" sx={{ fontWeight: 'medium' }}>
                      {noticeTime === 'Other' ? noticeTimeOther : noticeTime}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="md" component="p" sx={{ fontWeight: 'medium' }}>Side Assignments Availability</Typography>
                    <Typography variant="sm" component="p" sx={{ color: 'text.secondary', mb: 0.5 }}>
                      How many hours per week you can take on additional assignments
                    </Typography>
                    <Typography variant="md" component="p" sx={{ fontWeight: 'medium' }}>
                      {candidate?.sideAssignments || 'Up to 10 hours per week'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="md" component="p" sx={{ fontWeight: 'medium' }}>Engagement Type preference</Typography>
                    <Typography variant="sm" component="p" sx={{ color: 'text.secondary', mb: 0.5 }}>
                      Types of opportunities you are interested in
                    </Typography>
                    {engagementTypes.length > 0 && (
                      <div className='nc-badge-list' style={{ marginTop: 6 }}>
                        {engagementTypes.map((type) => (
                          <Chip key={type} label={type} />
                        ))}
                      </div>
                    )}
                  </Box>
                </>
              ) : (
                <>
                  <Box>
                    <Typography variant="md" component="p" sx={{ mb: 1 }}>Notice Time</Typography>
                    <Typography variant="sm" component="p" sx={{ mb: 2 }}>
                      Required notice period for leaving current position
                    </Typography>
                    <RadioGroup
                      value={noticeTime}
                      onChange={(e) => {
                        setNoticeTime(e.target.value);
                        if (e.target.value !== 'Other') {
                          setNoticeTimeOther('');
                        }
                      }}
                    >
                      <FormControlLabel value="2 Weeks" control={<Radio />} label="2 Weeks" />
                      <FormControlLabel value="1 Month" control={<Radio />} label="1 Month" />
                      <FormControlLabel value="Other" control={<Radio />} label="Other" />
                    </RadioGroup>
                    {noticeTime === 'Other' && (
                      <TextField
                        fullWidth
                        label="Other"
                        placeholder="Enter Other"
                        size="medium"
                        value={noticeTimeOther}
                        onChange={(e) => setNoticeTimeOther(e.target.value)}
                        sx={{ mt: 1, ml: 4 }}
                      />
                    )}
                  </Box>
                  <Box>
                    <Controller
                      name="sideAssignments"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="medium">
                          <InputLabel>Side Assignments Availability</InputLabel>
                          <Select {...field} label="Side Assignments Availability">
                            {SIDE_ASSIGNMENT_OPTIONS.map((option) => (
                              <MenuItem key={option} value={option}>{option}</MenuItem>
                            ))}
                          </Select>
                          <Typography variant="sm" component="p" sx={{ mt: 1 }}>
                            How many hours per week you can take on additional assignments
                          </Typography>
                        </FormControl>
                      )}
                    />
                  </Box>
                  <Box>
                    <Typography variant="md" component="p" sx={{ mb: 1 }}>Engagement Type preference</Typography>
                    <Typography variant="sm" component="p" sx={{ mb: 2 }}>
                      Types of opportunities you're interested in
                    </Typography>
                    <FormControl fullWidth size="medium" sx={{ mb: 2 }}>
                      <InputLabel>Engagement type preference</InputLabel>
                      <Select
                        label="Engagement type preference"
                        value=""
                        onChange={(e) => {
                          if (e.target.value && !engagementTypes.includes(e.target.value)) {
                            setEngagementTypes([...engagementTypes, e.target.value]);
                          }
                        }}
                      >
                        {ENGAGEMENT_TYPES.filter(type => !engagementTypes.includes(type)).map((type) => (
                          <MenuItem key={type} value={type}>{type}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {engagementTypes.map((type) => (
                        <Chip
                          key={type}
                          label={type}
                          onDelete={() => handleRemoveEngagementType(type)}
                          sx={{ backgroundColor: '#EAE7E6' }}
                        />
                      ))}
                    </Box>
                  </Box>
                </>
              )}
            </div>
          </CardContent>
        </Card>
        <Card id="profile-opportunities-card" sx={{ marginTop: 2 }}>
          <CardContent>
            <CardHeader
              title="Talent Pool Settings"
              subtitle="Give Native Coders permission to include your profile in the public Talent Pool section on the Native Coders website. Selected profiles may require a professional photo session before publication."
              >
              <PeopleOutlineOutlinedIcon />
            </CardHeader>
            <div className="nc-card-body">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1, mr: 2 }}>
                  <Typography variant="md" component="p">Permission to Include My Profile</Typography>
                  <Typography variant="sm" component="p">
                    I agree that Native Coders may feature my profile in the public Talent Pool section on the Native Coders website, making it visible to recruiters, companies, and other visitors to the website.
                  </Typography>
                  <Typography variant="sm" component="p" sx={{ mt: 1 }}>
                    You may withdraw your permission and request that your profile be unpublished at any time by contacting Native Coders.
                  </Typography>
                </Box>
                {!isEditMode ? (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={candidate?.includeInTalentPool !== undefined ? candidate.includeInTalentPool : false}
                        disabled
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#483C3B',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#483C3B',
                          },
                        }}
                      />
                    }
                    label=""
                    sx={{ m: 0 }}
                  />
                ) : (
                  <Controller
                    name="includeInTalentPool"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value}
                            onChange={field.onChange}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: '#483C3B',
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#483C3B',
                              },
                            }}
                          />
                        }
                        label=""
                        sx={{ m: 0 }}
                      />
                    )}
                  />
                )}
              </Box>
              <hr />
              <Box sx={{ py: 2, borderTop: '1px solid #EAE7E6' }}>
                <Typography variant="md" component="p">Currently Published Status</Typography>
                <Chip
                  color="success"
                  label={candidate?.includeInTalentPool ? "Published on the Native Coders website since March 2024" : "Not Published"}
                />
              </Box>
              <Box sx={{ py: 2, borderTop: '1px solid #EAE7E6' }}>
                {!isEditMode ? (
                  <>
                    <Typography variant="md" component="p">Video Presentation</Typography>
                    <Typography variant="sm" component="p">
                      Schedule time for a short video presentation (30-45 seconds)
                    </Typography>
                    <Typography variant="md" component="p">
                      {candidate?.videoPresentation || 'Maybe Later'}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Controller
                      name="videoPresentation"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="medium">
                          <InputLabel>Video Presentation</InputLabel>
                          <Select {...field} label="Video Presentation">
                            {VIDEO_PRESENTATION_OPTIONS.map((option) => (
                              <MenuItem key={option} value={option}>{option}</MenuItem>
                            ))}
                          </Select>
                          <Typography variant="sm" component="p" sx={{ mt: 1 }}>
                            Schedule time for a short video presentation (30-45 seconds)
                          </Typography>
                        </FormControl>
                      )}
                    />
                  </>
                )}
              </Box>
            </div>
          </CardContent>
        </Card>
        <Card id="profile-search-card" sx={{ marginTop: 2 }}>
          <CardContent>
            <CardHeader title="Search Status">
              <SearchOutlinedIcon />
            </CardHeader>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1, mr: 2 }}>
                <Typography variant="md" component="p">Profile Pause</Typography>
                <Typography variant="sm" component="p">
                  Include or exclude from searches.
                </Typography>
              </Box>
              {!isEditMode ? (
                <FormControlLabel
                  control={
                    <Switch
                      checked={candidate?.profilePause !== undefined ? candidate.profilePause : false}
                      disabled
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#483C3B',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#483C3B',
                        },
                      }}
                    />
                  }
                  label=""
                  sx={{ m: 0 }}
                />
              ) : (
                <Controller
                  name="profilePause"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#483C3B',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#483C3B',
                            },
                          }}
                        />
                      }
                      label=""
                      sx={{ m: 0 }}
                    />
                  )}
                />
              )}
            </Box>
          </CardContent>
        </Card>
      </div>
    </main>
  );
};

export default Profile;